import React, { useState, useEffect } from "react";
import MessengerLayout       from "../../layout/MessengerLayout";
import PerformanceSummary    from "../../components/performance/PerformanceSummary";
import GoalTracker           from "../../components/performance/GoalTracker";
import SelfReviewForm        from "../../components/performance/SelfReviewForm";
import PeerFeedbackSection   from "../../components/performance/PeerFeedbackSection";
import PerformanceHistory    from "../../components/performance/PerformanceHistory";
import MyPIPCard             from "../../components/performance/MyPIPCard";
import MyCareerCard          from "../../components/performance/MyCareerCard";
import MyTransferCard        from "../../components/performance/MyTransferCard";
import CompanyGoals          from "../../components/performance/CompanyGoals";

export default function PerformanceMessengerPage() {
  // Bumping this `key` value remounts the data-owning children so they
  // re-fetch — used to react to K.I.R.A action completion events.
  const [kiraVersion, setKiraVersion] = useState(0);

  // Session 2 — resolved performance profile. `null` means "no profile
  // matched / not loaded yet" → LEGACY behaviour: show ALL cards, gate
  // nothing (the safety path). A resolved profile gates which method
  // cards render, via its methods[] list. VISIBILITY only — every card
  // keeps its own fetch/handlers/state untouched.
  const [perfProfile, setPerfProfile] = useState(null);

  useEffect(() => {
    const handleKiraAction = (e) => {
      const t = e.detail?.action_type || "";
      if (["goal_create", "self_review", "manager_review"].includes(t)) {
        setKiraVersion(v => v + 1);
      }
    };
    window.addEventListener("kira_action_complete", handleKiraAction);
    return () => window.removeEventListener("kira_action_complete", handleKiraAction);
  }, []);

  useEffect(() => {
    const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
    const token = localStorage.getItem("token");
    // Best-effort: any failure / null profile → leave perfProfile null →
    // show-all. Never let profile resolution hide a card on error.
    fetch(`${API}/api/performance/my-profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => setPerfProfile(d?.profile ?? null))
      .catch(() => setPerfProfile(null));
  }, []);

  // methods = null (no resolved profile) → showMethod() is always true
  // (legacy show-all). With a profile, only its methods[] cards show.
  const methods = perfProfile?.methods || null;
  const showMethod = (m) => !methods || methods.includes(m);

  return (
    <MessengerLayout>
      <div className="p-6 space-y-5 text-white">

        <div>
          <h1 className="text-2xl font-bold text-[#00ff90]">Performance</h1>
          <p className="text-white/40 text-sm mt-0.5">Goals, self review, peer feedback and history</p>
          {/* When a profile resolves, surface its name + stage flow (additive). */}
          {perfProfile && (
            <p className="text-white/30 text-xs mt-1">
              Profile: <span className="text-[#00ff90]/70">{perfProfile.name}</span>
              {perfProfile.stages?.length > 0 && (
                <span className="text-white/25"> · {perfProfile.stages.join(" → ")}</span>
              )}
            </p>
          )}
        </div>

        {/* My PIP (staff, read-only) — self-hides when the caller has no
            active/extended plan, so it adds nothing for most staff. */}
        <MyPIPCard key={`pip-${kiraVersion}`} />

        {/* My Career events (staff, read-only) — self-hides when the caller
            has no career events. */}
        <MyCareerCard key={`career-${kiraVersion}`} />

        {/* My Transfers (staff, read-only) — self-hides when none. */}
        <MyTransferCard key={`transfer-${kiraVersion}`} />

        {/* Company (org-level) goals — READ ONLY context for goal setting.
            Sourced from /org-goals/visible, which serves ORG-type goals
            only; department/team goals stay admin-only. Not profile-gated:
            company objectives aren't an evaluation method. Self-hides when
            no company goals are set. */}
        <CompanyGoals />

        {/* Summary + Goals (KRA_KPI) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <PerformanceSummary key={`sum-${kiraVersion}`} />
          {showMethod("KRA_KPI") && <GoalTracker key={`goals-${kiraVersion}`} />}
        </div>

        {/* Self review + Peer feedback (360) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <SelfReviewForm key={`self-${kiraVersion}`} />
          {showMethod("360") && <PeerFeedbackSection key={`peer-${kiraVersion}`} />}
        </div>

        {/* Full-width history */}
        <PerformanceHistory key={`hist-${kiraVersion}`} />

      </div>
    </MessengerLayout>
  );
}